<template>
  <div>
    <div :class="['section-title', { active: isActive }]">
      <h3>Pilih Target Perusahaan</h3>
    </div>
    <div v-if="isActive" class="choose-target-company">
      <div class="form-container">
        <div class="form-group-wrapper">
          <!-- Start Info Box -->
          <div class="blue-info-box mb-2">
            <div>
              <img src="@/assets/icon/warning-circle-blue.svg" alt="warning" />
            </div>
            <p class="label">
              Proses ini akan menghentikan order lama dan membuat pembaruan
              order baru untuk Lessee Target.
            </p>
          </div>
          <!-- End Info Box -->

          <!-- Pilih Lessee Awal -->
          <div class="mb-3">
            <SingleSelectV2
              :disabled="disabledInput"
              :value="companyIdOriginal"
              :options="originalCompanyOptions"
              label="Pilih Lessee Awal"
              placeholder="Pilih Nama Lessee Awal"
              @change="updateField($event, 'updateCompanyIdOriginal')"
            />
          </div>

          <!-- Sudah buat Lessee Baru? -->
          <div class="w-50 mb-3">
            <InputRadio
              :disabled="disabledInput"
              name="isNewCompany"
              label="Sudah buat Lessee Baru?"
              type="horizontal"
              styleType="button"
              :value="isNewCompany"
              :options="[
                { label: 'Sudah', value: false },
                { label: 'Belum', value: true },
              ]"
              @change="updateIsNewCompany($event)"
              :customClass="{ labelClass: 'm-0' }"
            />
          </div>

          <div class="mb-3" v-if="isNewCompany === false">
            <div class="mb-2">
              <SingleSelectV2
                :disabled="disabledInput"
                :value="companyIdTarget"
                :options="targetCompanyOptions"
                label="Pilih Lessee Target"
                placeholder="Pilih Lessee Target"
                @change="updateField($event, 'updateCompanyIdTarget')"
              />
            </div>
            <template v-if="companyIdTarget">
              <div v-if="!showCompanyInfo" :class="['info-panel-close']">
                <div
                  @click="toggleCompanyInfo"
                  style="width: fit-content; cursor: pointer"
                >
                  Lihat atau Update Info klik disini
                </div>
              </div>
              <div v-if="showCompanyInfo" :class="['info-panel-open']">
                <div
                  @click="toggleCompanyInfo"
                  style="width: fit-content; cursor: pointer"
                >
                  Tutup info klik disini
                  <!-- show fa close icon -->
                  <span style="font-size: 16px">&#120;</span>
                </div>
              </div>
            </template>
          </div>

          <div v-if="isNewCompany || (companyIdTarget && showCompanyInfo)">
            <h4 class="section-title" v-if="isNewCompany">
              Buat Baru Lessee Target
            </h4>

            <div
              v-if="!isNewCompany && !disabledInput"
              class="warning-company-form mb-4"
            >
              <p class="label">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <circle
                    cx="8"
                    cy="8"
                    r="7"
                    stroke="#973c00"
                    stroke-width="1.5"
                  />
                  <text
                    x="8"
                    y="11"
                    text-anchor="middle"
                    font-size="10"
                    fill="#973c00"
                  >
                    !
                  </text>
                </svg>
                <strong>Perhatian:</strong> Mengubah atau mereset data PIC/Info
                Perusahaan di sini akan ikut mengubah data utama company.
              </p>
            </div>

            <!-- Buat Baru Lessee Target Section -->
            <div class="buat-baru-section mb-4">
              <div class="buat-baru-section-title">
                <h6 class="m-0">PIC Perusahaan</h6>
              </div>
              <div class="buat-baru-section-form">
                <!-- PIC Name -->
                <div class="inline-group">
                  <div class="field-label">
                    <span class="field-name">PIC Name</span>
                    <span class="field-separator">:</span>
                  </div>
                  <div>
                    <InputText
                      id="targetName"
                      :value="localData.newLesseeData.picName"
                      :disabled="disabledInput"
                      placeholder="PIC Name"
                      @change="updateTargetLesseeField('picName', $event)"
                    />
                    <p
                      v-if="$v.localData.newLesseeData.picName.$error"
                      class="text-danger mb-0"
                      style="font-size: 12px"
                    >
                      PIC Name must be filled
                    </p>
                  </div>
                </div>

                <!-- PIC Phone -->
                <div class="inline-group">
                  <div class="field-label">
                    <span class="field-name">PIC Phone</span>
                    <span class="field-separator">:</span>
                  </div>
                  <div>
                    <InputText
                      id="targetPhone"
                      :value="localData.newLesseeData.picPhone"
                      :disabled="disabledInput"
                      placeholder="PIC Phone"
                      :parser="(val) => val.replace(/[^0-9+]/g, '')"
                      @change="updateTargetLesseeField('picPhone', $event)"
                    />
                    <p
                      v-if="$v.localData.newLesseeData.picPhone.$error"
                      class="text-danger mb-0"
                      style="font-size: 12px"
                    >
                      PIC Phone must be filled and between 8-15 characters
                    </p>
                  </div>
                </div>

                <!-- PIC Division -->
                <div class="inline-group">
                  <div class="field-label">
                    <span class="field-name">PIC Division</span>
                    <span class="field-separator">:</span>
                  </div>
                  <div>
                    <InputText
                      id="targetDivision"
                      :value="localData.newLesseeData.picDivision"
                      :disabled="disabledInput"
                      placeholder="PIC Division"
                      @change="updateTargetLesseeField('picDivision', $event)"
                    />
                    <p
                      v-if="$v.localData.newLesseeData.picDivision.$error"
                      class="text-danger mb-0"
                      style="font-size: 12px"
                    >
                      PIC Division must be filled
                    </p>
                  </div>
                </div>

                <!-- PIC Position -->
                <div class="inline-group">
                  <div class="field-label">
                    <span class="field-name">PIC Position</span>
                    <span class="field-separator">:</span>
                  </div>
                  <div>
                    <InputText
                      id="targetPosition"
                      :value="localData.newLesseeData.picPosition"
                      :disabled="disabledInput"
                      placeholder="PIC Position"
                      @change="updateTargetLesseeField('picPosition', $event)"
                    />
                    <p
                      v-if="$v.localData.newLesseeData.picPosition.$error"
                      class="text-danger mb-0"
                      style="font-size: 12px"
                    >
                      PIC Position must be filled
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div class="buat-baru-section">
              <div class="buat-baru-section-title">
                <h6 class="m-0">Info Perusahaan</h6>
              </div>
              <div class="buat-baru-section-form">
                <!-- Name -->
                <div class="inline-group">
                  <div class="field-label">
                    <span class="field-name">Name</span>
                    <span class="field-separator">:</span>
                  </div>
                  <div>
                    <InputText
                      id="targetName"
                      :value="localData.newCompanyData.companyName"
                      :disabled="disabledInput"
                      placeholder="PT Name"
                      @change="updateTargetCompanyField('companyName', $event)"
                    />
                    <p
                      v-if="$v.localData.newCompanyData.companyName.$error"
                      class="text-danger mb-0"
                      style="font-size: 12px"
                    >
                      Name must be filled
                    </p>
                  </div>
                </div>

                <!-- Province -->
                <div class="inline-group">
                  <div class="field-label">
                    <span class="field-name">Province</span>
                    <span class="field-separator">:</span>
                  </div>
                  <div>
                    <SingleSelect
                      id="targetProvince"
                      :value="localData.newCompanyData.provinceName"
                      :options="getProvincesSelectByNameOptions"
                      :disabled="disabledInput"
                      @change="updateTargetCompanyField('provinceName', $event)"
                    />
                    <p
                      v-if="$v.localData.newCompanyData.provinceName.$error"
                      class="text-danger mb-0"
                      style="font-size: 12px"
                    >
                      Province must be filled
                    </p>
                  </div>
                </div>

                <!-- City -->
                <div class="inline-group">
                  <div class="field-label">
                    <span class="field-name">City</span>
                    <span class="field-separator">:</span>
                  </div>
                  <div>
                    <SingleSelect
                      id="targetCity"
                      :disabled="disabledInput"
                      :value="localData.newCompanyData.cityName"
                      :options="getCitySelectByNameOptions"
                      @change="updateTargetCompanyField('cityName', $event)"
                    />
                    <p
                      v-if="$v.localData.newCompanyData.cityName.$error"
                      class="text-danger mb-0"
                      style="font-size: 12px"
                    >
                      City must be filled
                    </p>
                  </div>
                </div>

                <!-- District -->
                <div class="inline-group">
                  <div class="field-label">
                    <span class="field-name">District</span>
                    <span class="field-separator">:</span>
                  </div>
                  <div>
                    <SingleSelect
                      id="targetDistrict"
                      :value="localData.newCompanyData.districtName"
                      :options="getDistrictSelectByNameOptions"
                      :disabled="disabledInput"
                      @change="updateTargetCompanyField('districtName', $event)"
                    />
                    <p
                      v-if="$v.localData.newCompanyData.districtName.$error"
                      class="text-danger mb-0"
                      style="font-size: 12px"
                    >
                      District must be filled
                    </p>
                  </div>
                </div>

                <!-- Village -->
                <div class="inline-group">
                  <div class="field-label">
                    <span class="field-name">Village</span>
                    <span class="field-separator">:</span>
                  </div>
                  <div>
                    <InputText
                      id="targetVillage"
                      :value="localData.newCompanyData.village"
                      placeholder="Village"
                      :disabled="disabledInput"
                      @change="updateTargetCompanyField('village', $event)"
                    />
                    <p
                      v-if="$v.localData.newCompanyData.village.$error"
                      class="text-danger mb-0"
                      style="font-size: 12px"
                    >
                      Village must be filled
                    </p>
                  </div>
                </div>

                <!-- Zip Code -->
                <div class="inline-group">
                  <div class="field-label">
                    <span class="field-name">Zip Code</span>
                    <span class="field-separator">:</span>
                  </div>
                  <div>
                    <InputText
                      id="targetZipCode"
                      :value="localData.newCompanyData.postalCode"
                      placeholder="12930"
                      :disabled="disabledInput"
                      @change="updateTargetCompanyField('postalCode', $event)"
                      :parser="(val) => val.replace(/[^0-9]/g, '')"
                    />
                    <p
                      v-if="$v.localData.newCompanyData.postalCode.$error"
                      class="text-danger mb-0"
                      style="font-size: 12px"
                    >
                      Zip Code must be filled and max 5 characters
                    </p>
                  </div>
                </div>

                <!-- Address -->
                <div class="inline-group">
                  <div class="field-label">
                    <span class="field-name">Address</span>
                    <span class="field-separator">:</span>
                  </div>
                  <div>
                    <InputTextarea
                      id="targetAddress"
                      :value="localData.newCompanyData.companyAddress"
                      placeholder="Address"
                      :disabled="disabledInput"
                      :rows="3"
                      @change="
                        updateTargetCompanyField('companyAddress', $event)
                      "
                    />
                    <p
                      v-if="$v.localData.newCompanyData.companyAddress.$error"
                      class="text-danger mb-0"
                      style="font-size: 12px"
                    >
                      Address must be filled and min 3 characters
                    </p>
                  </div>
                </div>

                <!-- Akta Address -->
                <div class="inline-group">
                  <div class="field-label">
                    <span class="field-name">Akta Address</span>
                    <span class="field-separator">:</span>
                  </div>
                  <div>
                    <InputTextarea
                      id="targetAktaAddress"
                      :value="localData.newCompanyData.companyAktaAddress"
                      placeholder="Akta Address"
                      :rows="3"
                      :disabled="disabledInput"
                      @change="
                        updateTargetCompanyField('companyAktaAddress', $event)
                      "
                    />
                    <p
                      v-if="
                        $v.localData.newCompanyData.companyAktaAddress.$error
                      "
                      class="text-danger mb-0"
                      style="font-size: 12px"
                    >
                      Akta Address must be filled and min 3 characters
                    </p>
                  </div>
                </div>

                <!-- Citizenship -->
                <div class="inline-group">
                  <div class="field-label">
                    <span class="field-name">Citizenship</span>
                    <span class="field-separator">:</span>
                  </div>
                  <div>
                    <SingleSelect
                      id="targetCitizenship"
                      :value="localData.newCompanyData.citizenship"
                      :options="citizenshipOptions"
                      :disabled="disabledInput"
                      @change="updateTargetCompanyField('citizenship', $event)"
                    />
                    <p
                      v-if="$v.localData.newCompanyData.citizenship.$error"
                      class="text-danger mb-0"
                      style="font-size: 12px"
                    >
                      Citizenship must be filled
                    </p>
                  </div>
                </div>

                <div>
                  <div class="inline-group" style="flex: 1">
                    <div class="field-label">
                      <span class="field-name">Company Name (NPWP)</span>
                      <span class="field-separator">:</span>
                    </div>
                    <div>
                      <div class="d-flex align-items-end">
                        <div style="flex: 1">
                          <InputText
                            id="companyNpwp16"
                            :value="localData.newCompanyData.companyNameNPWP"
                            placeholder="PT ABCD"
                            :disabled="disabledNotDraft"
                            @change="
                              updateTargetCompanyField(
                                'companyNameNPWP',
                                $event
                              )
                            "
                          />
                        </div>
                      </div>
                      <p
                        v-if="
                          $v.localData.newCompanyData.companyNameNPWP.$error
                        "
                        class="text-danger mb-0"
                        style="font-size: 12px"
                      >
                        Company Name (NPWP) must be filled
                      </p>
                    </div>
                  </div>
                </div>

                <!-- NPWP -->
                <div>
                  <div class="inline-group" style="flex: 1">
                    <div class="field-label">
                      <span class="field-name">NPWP</span>
                      <span class="field-separator">:</span>
                    </div>
                    <div>
                      <div class="d-flex align-items-end">
                        <div style="flex: 1">
                          <InputText
                            id="companyNpwp16"
                            :value="localData.newCompanyData.companyNpwp16"
                            placeholder="NPWP"
                            :disabled="disabledInput"
                            @change="
                              updateTargetCompanyField('companyNpwp16', $event)
                            "
                            :parser="(val) => val.replace(/[^0-9]/g, '')"
                          />
                        </div>
                      </div>
                      <p
                        v-if="$v.localData.newCompanyData.companyNpwp16.$error"
                        class="text-danger mb-0"
                        style="font-size: 12px"
                      >
                        NPWP must be filled and has 16 digit characters
                      </p>
                    </div>
                  </div>
                </div>

                <!-- NTKU -->
                <div class="inline-group" style="flex: 1">
                  <div class="field-label">
                    <span class="field-name">NITKU (Jika Ada)</span>
                    <span class="field-separator">:</span>
                  </div>
                  <div>
                    <div class="d-flex align-items-end">
                      <div style="flex: 1">
                        <InputText
                          id="companyNpwp22"
                          :disabled="disabledInput"
                          :value="localData.newCompanyData.companyNpwp22"
                          placeholder="NITKU"
                          :parser="(val) => val.replace(/[^0-9]/g, '')"
                          @change="
                            updateTargetCompanyField('companyNpwp22', $event)
                          "
                        />
                      </div>
                    </div>
                    <p
                      v-if="$v.localData.newCompanyData.companyNpwp22.$error"
                      class="text-danger mb-0"
                      style="font-size: 12px"
                    >
                      NITKU must have 22 digits number
                    </p>
                  </div>
                </div>

                <!-- Phone -->
                <div class="inline-group">
                  <div class="field-label">
                    <span class="field-name">Phone</span>
                    <span class="field-separator">:</span>
                  </div>
                  <div>
                    <InputText
                      id="targetPhone"
                      :value="localData.newCompanyData.companyPhone"
                      placeholder="Phone"
                      :disabled="disabledInput"
                      @change="updateTargetCompanyField('companyPhone', $event)"
                      :parser="(val) => val.replace(/[^0-9+]/g, '')"
                    />
                    <p
                      v-if="$v.localData.newCompanyData.companyPhone.$error"
                      class="text-danger mb-0"
                      style="font-size: 12px"
                    >
                      Phone must be filled and between 8-15 characters
                    </p>
                  </div>
                </div>

                <!-- Source -->
                <div class="inline-group mb-3">
                  <div class="field-label">
                    <span class="field-name">Source</span>
                    <span class="field-separator">:</span>
                  </div>
                  <div>
                    <SingleSelect
                      id="targetSource"
                      :disabled="disabledInput"
                      :value="localData.newCompanyData.source"
                      :options="getLesseeSourceSelectOptions"
                      @change="updateTargetCompanyField('source', $event)"
                    />
                    <p
                      v-if="$v.localData.newCompanyData.source.$error"
                      class="text-danger mb-0"
                      style="font-size: 12px"
                    >
                      Source must be filled
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <!-- Action Buttons -->
            <component-guard
              :requiredPermissions="['28.2', '28.3']"
              operator="OR"
            >
              <div class="action-buttons mt-3" v-if="isNewCompanyDataChanged">
                <button class="btn btn-danger" @click="handleResetData">
                  Reset Data
                </button>
                <button class="btn btn-success" @click="handleSaveCompanyData">
                  Simpan Lessee Target
                </button>
              </div>
            </component-guard>
          </div>
        </div>
      </div>
      <br />
    </div>
  </div>
</template>

<script>
import {
  InputText,
  SingleSelect,
  InputRadio,
  InputTextarea,
  InputPhone,
  SingleSelectV2,
} from "@/components/Input";
import { mapGetters, mapMutations, mapActions } from "vuex";
import mixin from "@/components/RentAdjustment/mixin";
import { ComponentGuard } from "@/components/Permissions";

import { lesseeTargetValidations } from "@/components/RentAdjustment/Utils";

import { alert } from "@/utils";

export default {
  name: "ChooseTargetCompany",
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    InputText,
    SingleSelect,
    InputRadio,
    InputTextarea,
    InputPhone,
    SingleSelectV2,
    ComponentGuard,
  },
  mixins: [mixin],
  data() {
    const templateLocalData = {
      newCompanyData: {
        companyName: "",
        companyNameNPWP: "",
        provinceName: "",
        cityName: "",
        districtName: "",
        village: "",
        postalCode: "",
        companyAddress: "",
        companyAktaAddress: "",
        citizenship: "",
        companyNpwp16: "",
        companyNpwp22: "",
        companyPhone: "",
        source: "",
      },
      newLesseeData: {
        picName: "",
        picPhone: "",
        picDivision: "",
        picPosition: "",
      },
    };
    return {
      unchangedLocalData: templateLocalData,
      localData: templateLocalData,
      citizenshipOptions: [
        { label: "WNI", value: "wni" },
        { label: "WNA", value: "wna" },
      ],
      showCompanyInfo: false,
    };
  },
  validations: {
    localData: lesseeTargetValidations,
  },
  computed: {
    ...mapGetters("others", [
      "getProvincesSelectByNameOptions",
      "getCitySelectByNameOptions",
      "getDistrictSelectByNameOptions",
      "getProvinces",
      "getCities",
    ]),
    ...mapGetters("lessee", [
      "getLesseeListSelectOptions",
      "getLesseeSourceSelectOptions",
    ]),
    ...mapGetters("rentAdjustment", {
      getCompanyLesseeData: "getCompanyLesseeData",
      companyIdOriginal: "getCompanyIdOriginal",
      companyIdTarget: "getCompanyIdTarget",
      isNewCompany: "getIsNewCompany",
      newCompanyData: "getNewCompanyData",
      currentStep: "getCurrentStep",
      transferUnitId: "getId",
      transferUnitStatus: "getStatus",
      disabledNotDraft: "getDisabledNotDraft",
      targetCompany: "getTargetCompany",
      pageType: "getType",
      getCompanyTargetOptions: "getCompanyTargetOptions",
    }),
    originalCompanyOptions() {
      return this.getLesseeListSelectOptions.filter(
        (option) => option.value !== this.companyIdTarget
      );
    },
    targetCompanyOptions() {
      return this.getCompanyTargetOptions.filter(
        (option) => option.value !== this.companyIdOriginal
      );
    },
    isNewCompanyDataChanged() {
      const companyDataKeys = Object.keys(this.localData.newCompanyData);
      for (const key of companyDataKeys) {
        if (
          this.localData.newCompanyData[key] !==
          this.unchangedLocalData.newCompanyData[key]
        ) {
          return true;
        }
      }
      const lesseeDataKeys = Object.keys(this.localData.newLesseeData);
      for (const key of lesseeDataKeys) {
        if (
          this.localData.newLesseeData[key] !==
          this.unchangedLocalData.newLesseeData[key]
        ) {
          return true;
        }
      }
    },
  },
  methods: {
    ...mapMutations("others", {
      clearCities: "CLEAR_CITIES",
      clearDistricts: "CLEAR_DISTRICTS",
    }),
    ...mapMutations("rentAdjustment", {
      resetNewCompanyData: "RESET_NEW_COMPANY_DATA",
      resetCompanyLesseeData: "RESET_COMPANY_LESSEE_DATA",
      setCompanyIdTarget: "SET_COMPANY_ID_TARGET",
      setIsNewCompanyDataChanged: "SET_IS_NEW_COMPANY_DATA_CHANGED",
      setTransferAllUnits: "SET_TRANSFER_ALL_UNITS",
    }),
    ...mapActions("rentAdjustment", {
      updateIsNewCompanyAction: "updateIsNewCompany",
      updateNewCompanyFieldAction: "updateNewCompanyField",
      updateCompanyIdTargetAction: "updateCompanyIdTarget",
      getLesseeOrderRenting: "getLesseeOrderRenting",
      updateNewCompanyData: "updateNewCompanyData",
      updateCompanyIdOriginal: "updateCompanyIdOriginal",
      fetchCompanyTargetList: "fetchCompanyTargetList",
    }),
    ...mapActions("lessee", ["fetchLesseeList", "fetchLesseeSourceOptions"]),
    ...mapActions("others", [
      "fetchCities",
      "fetchDistricts",
      "fetchProvinces",
    ]),
    toggleCompanyInfo() {
      this.showCompanyInfo = !this.showCompanyInfo;
    },
    updateTargetCompanyField(field, value) {
      switch (field) {
        case "provinceName":
          this.localData.newCompanyData = {
            ...this.localData.newCompanyData,
            cityName: "",
            districtName: "",
          };
          const province = this.getProvinces.find(
            (province) => province.provinceName === value
          );
          const provinceId = province ? province.provinceId : null;
          this.fetchCities(provinceId);
          break;
        case "cityName":
          this.localData.newCompanyData = {
            ...this.localData.newCompanyData,
            districtName: "",
          };
          const city = this.getCities.find((city) => city.kabKotName === value);
          const cityId = city ? city.kabKotId : null;
          this.fetchDistricts(cityId);
          break;
        default:
          break;
      }
      this.localData.newCompanyData = {
        ...this.localData.newCompanyData,
        [field]: value,
      };
    },
    updateTargetLesseeField(field, value) {
      this.localData.newLesseeData = {
        ...this.localData.newLesseeData,
        [field]: value,
      };
    },
    async updateField(value, field) {
      if (field === "updateCompanyIdOriginal") {
        this.updateCompanyIdOriginal(value);
        await this.getLesseeOrderRenting();
        if (this.pageType === "change-name") {
          this.setTransferAllUnits();
        }
        return;
      }
      const { isSuccess, data } = await this.updateCompanyIdTargetAction(value);
      if (isSuccess) {
        this.localData = {
          newCompanyData: { ...data.newCompanyData },
          newLesseeData: { ...data.newLesseeData },
        };
        this.unchangedLocalData = {
          newCompanyData: { ...data.newCompanyData },
          newLesseeData: { ...data.newLesseeData },
        };
        if (this.companyIdOriginal) {
          await this.getLesseeOrderRenting();
          if (this.pageType === "change-name") {
            this.setTransferAllUnits();
          }
        }
      }
    },
    async updateIsNewCompany(value) {
      let shouldChange = true;
      if (this.isNewCompanyDataChanged) {
        const responseAlert = await alert().fire({
          icon: "warning",
          title: "Apakah Anda Yakin?",
          text: `Data yang sudah anda diisi akan hilang!`,
          showCancelButton: true,
          confirmButtonText: "Ya, Lanjutkan!",
          cancelButtonText: "Batal",
        });
        shouldChange = responseAlert.isConfirmed;
      }
      if (!shouldChange) return;

      this.resetForm();
      this.updateIsNewCompanyAction({ value });
      this.showCompanyInfo = false;
    },
    updateNewCompanyField(field, value) {
      this.updateNewCompanyFieldAction({ field, value });
    },
    handleResetData() {
      alert({
        confirmButtonColor: "red",
      })
        .fire({
          icon: "warning",
          title: "Apakah Anda Yakin?",
          text: "Ingin mengatur ulang semua data dalam formulir ini?",
          showCancelButton: true,
          confirmButtonText: "Reset Sekarang",
          cancelButtonText: "Batal",
        })
        .then(async (result) => {
          if (result.isConfirmed) {
            this.resetForm();
            await this.setFormData(this.isNewCompany);
          }
        });
    },
    async handleSaveCompanyData() {
      this.$v.$touch();
      const isValid = !this.$v.$invalid;
      if (!isValid) {
        alert().fire({
          icon: "error",
          title: "Error",
          text: "Please fill in all required fields.",
          confirmButtonText: "Okay",
        });
        return;
      }

      const { isSuccess, message } = await this.updateNewCompanyData({
        newCompanyData: this.localData.newCompanyData,
        newLesseeData: {
          ...this.localData.newLesseeData,
        },
      });
      if (!isSuccess) {
        alert().fire({
          icon: "error",
          title: "Error",
          text: message || "Failed to save Lessee Target data.",
          confirmButtonText: "Okay",
        });
        return;
      }
      this.setFormData(this.isNewCompany);
      await alert().fire({
        icon: "success",
        title: "Success",
        text: "Lessee Target data has been saved",
        confirmButtonText: "Okay",
      });
      await this.getLesseeOrderRenting();
      if (this.pageType === "change-name") {
        this.setTransferAllUnits();
      }
    },

    resetForm() {
      const newCompanyData = {
        companyName: "",
        provinceName: "",
        cityName: "",
        districtName: "",
        village: "",
        postalCode: "",
        companyAddress: "",
        companyAktaAddress: "",
        citizenship: "",
        companyNpwp16: "",
        companyNpwp22: "",
        companyPhone: "",
        source: "",
      };
      const newLesseeData = {
        picName: "",
        picPhone: "",
        picDivision: "",
        picPosition: "",
      };
      this.localData = {
        newCompanyData: { ...newCompanyData },
        newLesseeData: { ...newLesseeData },
      };
      this.unchangedLocalData = {
        newCompanyData: { ...newCompanyData },
        newLesseeData: { ...newLesseeData },
      };
      this.$v.$reset();
    },
    async fetchLocations(provinceName, cityName) {
      const province = this.getProvinces.find(
        (item) => item.provinceName === provinceName
      );
      const cities = await this.fetchCities(province.provinceId);
      const city = cities.find((item) => item.kabKotName === cityName);
      await this.fetchDistricts(city.kabKotId);
    },
    async setFormData(isNewCompany) {
      const { new: newCompany, existing } = this.targetCompany;
      if (isNewCompany) {
        const { newCompanyData, newLesseeData, companyIdTarget } = newCompany;
        if (!newCompanyData.companyName) return;
        this.setCompanyIdTarget(companyIdTarget);
        this.localData = {
          newCompanyData: { ...newCompanyData },
          newLesseeData: { ...newLesseeData },
        };
        this.unchangedLocalData = {
          newCompanyData: { ...newCompanyData },
          newLesseeData: { ...newLesseeData },
        };
        await this.fetchLocations(
          newCompanyData.provinceName,
          newCompanyData.cityName
        );
      } else {
        const { newCompanyData, newLesseeData, companyIdTarget } = existing;
        if (!newCompanyData || !newCompanyData.companyName) return;
        this.setCompanyIdTarget(companyIdTarget);
        this.localData = {
          newCompanyData: { ...newCompanyData },
          newLesseeData: { ...newLesseeData },
        };
        this.unchangedLocalData = {
          newCompanyData: { ...newCompanyData },
          newLesseeData: { ...newLesseeData },
        };
        await this.fetchLocations(
          newCompanyData.provinceName,
          newCompanyData.cityName
        );
      }
    },
  },
  watch: {
    isNewCompany: {
      async handler(newVal) {
        await this.setFormData(newVal);
      },
    },
    isNewCompanyDataChanged(newVal) {
      this.setIsNewCompanyDataChanged(newVal);
    },
  },
  async mounted() {
    this.fetchLesseeList();
    this.fetchCompanyTargetList();
    this.fetchLesseeSourceOptions();
    await this.fetchProvinces();

    // This funtion is called here because
    // when moving between detail and verification tab,
    // the localData will be empty
    await this.setFormData(this.isNewCompany);
  },
};
</script>

<style scoped>
.section-title {
  flex: 1;
}

.section-title h3 {
  font-size: 16px;
  font-weight: 600;
  color: #2e3646;
  opacity: 0.4;
  text-transform: capitalize;
  margin-bottom: 12px;
}

.section-title.active h3 {
  opacity: 1;
}

.choose-target-company {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-container {
  background-color: #ffffff;
  border: 1px solid #d1d9e2;
  border-radius: 8px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group-wrapper {
  display: flex;
  flex-direction: column;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #2e3646;
  text-transform: capitalize;
  margin-bottom: 12px;
}

.buat-baru-section-form {
  padding: 8px 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.buat-baru-section-title {
  font-size: 16px;
  font-weight: 600;
  color: #2e3646;
  padding: 12px 20px;
  border-bottom: 1px solid #d1d9e2;
}

.buat-baru-section {
  background-color: #ffffff;
  border: 1px solid #d1d9e2;
  border-radius: 8px;
}

.inline-group {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.field-label {
  display: flex;
  gap: 12px;
  align-items: start;
  min-width: 110px;
  margin-top: 8px;
}

.field-name {
  font-size: 16px;
  font-weight: 400;
  color: #2e3646;
  /* white-space: nowrap; */
  width: 130px;
}

.field-separator {
  font-size: 16px;
  font-weight: 500;
  color: #2e3646;
  white-space: nowrap;
}

.inline-group > div:last-child {
  flex: 1;
}

.action-buttons {
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}

.footer-buttons {
  display: flex;
  gap: 12px;
  justify-content: space-between;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-primary {
  background-color: #247fec;
  color: #ffffff;
}

.btn-primary:hover {
  background-color: #1a5db8;
}

.btn-secondary {
  background-color: #ffffff;
  color: #2e3646;
  border: 1px solid #d1d9e2;
}

.btn-secondary:hover {
  background-color: #f8f9fa;
}

.btn-danger {
  background-color: #dc3545;
  color: #ffffff;
}

.btn-danger:hover {
  background-color: #c82333;
}

.btn-success {
  background-color: #247fec;
  color: #ffffff;
}

.btn-success:hover {
  background-color: #1a5db8;
}

.info-panel-close {
  color: #247fec;
  text-decoration: underline;
  font-size: 14px;
  display: flex;
  justify-content: flex-end;
}

.info-panel-open {
  color: #6a7282;
  text-decoration: underline;
  font-size: 14px;
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .inline-group {
    flex-direction: column;
  }

  .field-label {
    min-width: auto;
  }

  .action-buttons {
    justify-content: flex-start;
  }
}

.warning-company-form {
  display: flex;
  align-items: center;
  width: 100%;
  background-color: #fffbeb;
  padding: 12px 16px;
  border: 1px solid #fee685;
  border-radius: 8px;
}

.warning-company-form .label {
  font-size: 12px;
  font-weight: 500;
  color: #973c00;
  margin: 0;
}

.blue-info-box {
  color: #1d54b0;
  background-color: #eff8ff;
  border: 1px solid #92d4fe;
  padding: 12px;
  border-radius: 8px;
  font-size: 14px;
  display: flex;
  gap: 12px;
}

.blue-info-box p {
  margin: 0;
}
</style>



// WEBPACK FOOTER //
// src/components/RentAdjustment/FormTabs/Detail/DetailFormStep/ChooseTargetCompany/ChooseTargetCompany.vue